import { CANONICAL_URL, SITE_NAME, getSeoForPath } from "./seo";
import { APP_LINKS } from "./appStoreLinks";

/** Key pages Google may show as sitelinks — keep labels aligned with on-site nav */
export const SITE_NAV_SITELINKS = [
  {
    name: "How It Works",
    path: "/marketing",
    description:
      "Learn how Tydii laundry pickup and delivery works for wash and fold, dry cleaning, and tailoring.",
  },
  {
    name: "Become a Delivery Partner",
    path: "/delivery-partner",
    description:
      "Drive with Tydii. Flexible routes, pickup and delivery for local laundry businesses.",
  },
  {
    name: "Laundry Business Partners",
    path: "/laundry-business",
    description:
      "Partner your laundromat, dry cleaner, or tailor with Tydii and grow with more orders.",
  },
  {
    name: "Help & Support",
    path: "/support",
    description:
      "Contact Tydii support, get help with orders, and find answers to common questions.",
  },
  {
    name: "Privacy Policy",
    path: "/privacy-policy",
    description: "Tydii privacy policy, terms of service, and legal information.",
  },
];

const SCRIPT_ID = "tydii-structured-data";

export function buildOrganizationGraph() {
  return {
    "@type": "Organization",
    "@id": `${CANONICAL_URL}/#organization`,
    name: SITE_NAME,
    legalName: "Sembe LLC dba Tydii",
    url: CANONICAL_URL,
    logo: {
      "@type": "ImageObject",
      url: `${CANONICAL_URL}/logo512.png`,
      width: 512,
      height: 512,
    },
    description:
      "Tydii is an on-demand laundry pickup and delivery platform connecting customers with trusted local laundromats, dry cleaners, and tailors.",
    email: "help@tydii.io",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "help@tydii.io",
      availableLanguage: "English",
    },
  };
}

export function buildSiteNavigationList() {
  return {
    "@type": "ItemList",
    "@id": `${CANONICAL_URL}/#sitenavigation`,
    name: "TYDII site navigation",
    itemListElement: SITE_NAV_SITELINKS.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "WebPage",
        "@id": `${CANONICAL_URL}${item.path}`,
        name: item.name,
        description: item.description,
        url: `${CANONICAL_URL}${item.path}`,
      },
    })),
  };
}

export function buildMobileApplicationSchema({ id, name, description, iosUrl, androidUrl }) {
  return {
    "@type": "MobileApplication",
    "@id": `${CANONICAL_URL}/#${id}`,
    name,
    description,
    applicationCategory: "LifestyleApplication",
    operatingSystem: "iOS, Android",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    installUrl: iosUrl,
    downloadUrl: androidUrl,
    provider: { "@id": `${CANONICAL_URL}/#organization` },
  };
}

export function buildStructuredDataGraph(pathname = "/") {
  const seo = getSeoForPath(pathname);
  const canonical = `${CANONICAL_URL}${pathname === "/" ? "" : pathname.replace(/\/$/, "")}`;

  const breadcrumb =
    pathname === "/"
      ? null
      : {
          "@type": "BreadcrumbList",
          "@id": `${canonical}#breadcrumb`,
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: CANONICAL_URL,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: seo.title.replace(/\s*\|\s*TYDII$/i, "").trim(),
              item: canonical,
            },
          ],
        };

  const webPage = {
    "@type": "WebPage",
    "@id": `${canonical}#webpage`,
    url: canonical,
    name: seo.title,
    description: seo.description,
    isPartOf: { "@id": `${CANONICAL_URL}/#website` },
    about: { "@id": `${CANONICAL_URL}/#organization` },
    inLanguage: "en-US",
  };

  const graph = [
    buildOrganizationGraph(),
    {
      "@type": "WebSite",
      "@id": `${CANONICAL_URL}/#website`,
      url: CANONICAL_URL,
      name: SITE_NAME,
      description:
        "Laundry, dry cleaning, and tailoring delivery. Schedule a pickup and get clean clothes delivered to your door.",
      publisher: { "@id": `${CANONICAL_URL}/#organization` },
      inLanguage: "en-US",
    },
    buildSiteNavigationList(),
    buildMobileApplicationSchema({
      id: "customer-app",
      name: "TYDII Customer App",
      description:
        "Book laundry pickup and delivery, track orders, and pay securely from your phone.",
      iosUrl: APP_LINKS.customer.ios,
      androidUrl: APP_LINKS.customer.android,
    }),
    buildMobileApplicationSchema({
      id: "driver-app",
      name: "TYDII Driver App",
      description:
        "Accept delivery routes, navigate pickups and drop-offs, and manage earnings with Tydii.",
      iosUrl: APP_LINKS.driver.ios,
      androidUrl: APP_LINKS.driver.android,
    }),
    webPage,
  ];

  if (breadcrumb) graph.push(breadcrumb);

  if (pathname === "/") {
    graph.push({
      "@type": "LocalBusiness",
      "@id": `${CANONICAL_URL}/#localbusiness`,
      name: SITE_NAME,
      url: CANONICAL_URL,
      image: `${CANONICAL_URL}/logo512.png`,
      description:
        "On-demand laundry, dry cleaning, and tailoring pickup and delivery in Greater Boston.",
      areaServed: ["Cambridge, MA", "Somerville, MA", "Boston, MA", "Revere, MA"],
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

export function injectStructuredData(pathname) {
  if (typeof document === "undefined") return;

  const payload = buildStructuredDataGraph(pathname);
  let script = document.getElementById(SCRIPT_ID);

  if (!script) {
    script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(payload);
}
